"use client";

import { useRef, useState, useTransition, type ReactNode } from "react";
import { ListTodo } from "lucide-react";
import { useAppPathname as usePathname, useAppRouter as useRouter } from "@/lib/navigation";

import { TasksSidePanel } from "@/components/tasks-side-panel";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTaskRealtime } from "@/hooks/use-task-realtime";
import { cn } from "@/lib/utils";

const getTaskRoute = (taskId: string) => `/task-list/tasks/${encodeURIComponent(taskId)}`;

export function TaskListTasksShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const [, startTransition] = useTransition();
  const prefetchedTaskRoutes = useRef<Set<string> | null>(null);
  const [mobileTaskPanelPath, setMobileTaskPanelPath] = useState<string | null>(null);
  const isTaskDetailRoute = pathname.startsWith("/task-list/tasks/");
  const isMobileTaskPanelOpen = isMobile && isTaskDetailRoute && mobileTaskPanelPath === pathname;

  useTaskRealtime();

  const prefetchTaskRoute = (taskId: string) => {
    const route = getTaskRoute(taskId);
    const taskRoutes = prefetchedTaskRoutes.current ?? new Set<string>();
    prefetchedTaskRoutes.current = taskRoutes;

    if (taskRoutes.has(route)) {
      return;
    }

    taskRoutes.add(route);
    void router.prefetch(route);
  };

  const openMobileTaskPanel = () => {
    setMobileTaskPanelPath(pathname);
  };

  const closeMobileTaskPanel = () => {
    setMobileTaskPanelPath(null);
  };

  const handleTaskClick = (taskId: string) => {
    setMobileTaskPanelPath(null);
    startTransition(() => {
      router.push(getTaskRoute(taskId));
    });
  };

  return (
    <div className="flex h-dvh w-full overflow-hidden md:h-screen">
      <div className="hidden h-full shrink-0 lg:block">
        <TasksSidePanel
          onTaskClick={handleTaskClick}
          onTaskPrefetch={prefetchTaskRoute}
        />
      </div>

      {!isTaskDetailRoute ? (
        <div className="h-full w-full lg:hidden">
          <TasksSidePanel
            onTaskClick={handleTaskClick}
            onTaskPrefetch={prefetchTaskRoute}
            mobile
          />
        </div>
      ) : null}

      <Sheet
        open={isMobileTaskPanelOpen}
        onOpenChange={(open) => {
          if (open) {
            openMobileTaskPanel();
            return;
          }

          closeMobileTaskPanel();
        }}
      >
        <SheetContent
          side="left"
          className="w-[20rem] p-0 sm:max-w-[20rem]"
          showCloseButton={false}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Tareas</SheetTitle>
            <SheetDescription>Lista de tareas disponibles</SheetDescription>
          </SheetHeader>
          <TasksSidePanel
            onTaskClick={handleTaskClick}
            onTaskPrefetch={prefetchTaskRoute}
            mobile
            onMobileClose={closeMobileTaskPanel}
          />
        </SheetContent>
      </Sheet>

      <main
        className={cn(
          "min-w-0 flex-1",
          isTaskDetailRoute ? "relative block overflow-hidden" : "hidden overflow-auto lg:block",
        )}
      >
        {isTaskDetailRoute ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="fixed left-14 top-3 z-50 h-9 gap-1 border-gray-200 bg-white px-3 shadow-sm lg:hidden"
            onClick={openMobileTaskPanel}
          >
            <ListTodo className="h-4 w-4" />
            Tareas
          </Button>
        ) : null}

        <div
          className={cn(
            "min-w-0",
            isTaskDetailRoute
              ? "h-full max-lg:mt-12 max-lg:h-[calc(100dvh-3rem)]"
              : "h-full",
          )}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
